/* global globalThis */
// File payloads live in IndexedDB as Blobs. They are never encoded into JSON,
// localStorage or the compact SQLite project snapshot.
const DATABASE = 'upya_project_files';
const STORE = 'queued_files';
const MAX_FILES = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const accountKey = (value) => {
  if (value === undefined || value === null || !String(value).trim()) throw new Error('An account is required');
  return String(value);
};
const projectKey = (value) => {
  if (value === undefined || value === null || !String(value).trim()) throw new Error('A project is required');
  return String(value);
};
const keyFor = (account, project, id) => JSON.stringify([account, project, id]);

export function createIndexedDBFileStore(factory = globalThis.indexedDB) {
  if (!factory) throw new Error('IndexedDB is unavailable');
  const dbPromise = new Promise((resolve, reject) => {
    const request = factory.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      const files = db.createObjectStore(STORE, { keyPath: 'key' });
      files.createIndex('account', 'accountId');
      files.createIndex('project', ['accountId', 'projectId']);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Cannot open offline files'));
    request.onblocked = () => reject(new Error('Offline file database is blocked'));
  });
  const read = async (getRequest) => {
    const db = await dbPromise;
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE, 'readonly');
      const request = getRequest(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('Offline file read failed'));
      transaction.onabort = () => reject(transaction.error || new Error('Offline file read aborted'));
    });
  };
  const write = async (apply) => {
    const db = await dbPromise;
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE, 'readwrite');
      apply(transaction.objectStore(STORE));
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error || new Error('Offline file write failed'));
      transaction.onabort = () => reject(transaction.error || new Error('Offline file write aborted'));
    });
  };
  return {
    list(account, project) {
      return read((files) => project
        ? files.index('project').getAll(globalThis.IDBKeyRange.only([account, project]))
        : files.index('account').getAll(globalThis.IDBKeyRange.only(account)));
    },
    get(account, project, id) { return read((files) => files.get(keyFor(account, project, id))); },
    put(record) { return write((files) => { files.put(record); }); },
    remove(account, project, id) { return write((files) => { files.delete(keyFor(account, project, id)); }); },
    async clear(account) {
      const db = await dbPromise;
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE, 'readwrite');
        const cursor = transaction.objectStore(STORE).index('account').openCursor(globalThis.IDBKeyRange.only(account));
        cursor.onsuccess = () => { if (cursor.result) { cursor.result.delete(); cursor.result.continue(); } };
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error || new Error('Offline file purge failed'));
        transaction.onabort = () => reject(transaction.error || new Error('Offline file purge aborted'));
      });
    },
  };
}

export function createProjectFileQueue(getStore = () => createIndexedDBFileStore()) {
  let storePromise;
  let queue = Promise.resolve();
  const listeners = new Set();
  const active = new Set();
  const store = () => {
    if (!storePromise) storePromise = Promise.resolve().then(getStore).catch((error) => { storePromise = null; throw error; });
    return storePromise;
  };
  const serial = (work) => {
    const result = queue.then(work);
    queue = result.catch(() => {});
    return result;
  };
  const emit = (accountId, projectId, detail = {}) => listeners.forEach((listener) => {
    try { listener({ accountId, projectId, ...detail }); } catch (_) { /* Observer errors do not undo storage commits. */ }
  });
  const visible = (record) => ({ ...record, status: active.has(record.key) ? 'uploading' : record.status });
  return {
    listFiles(userId, projectId) {
      return serial(async () => (await (await store()).list(accountKey(userId), projectId ? projectKey(projectId) : null))
        .sort((a, b) => (a.queuedAt || '').localeCompare(b.queuedAt || '')).map(visible));
    },
    enqueueFile(userId, projectId, file, { category, serverCount = 0 } = {}) {
      return serial(async () => {
        const account = accountKey(userId); const project = projectKey(projectId);
        if (!(file instanceof Blob) || !file.size || file.size > MAX_FILE_SIZE) throw new Error('Invalid file size');
        const driver = await store();
        const existing = await driver.list(account, project);
        if (serverCount + existing.length >= MAX_FILES) throw new Error('File limit reached');
        const id = `u_${globalThis.crypto?.randomUUID?.().replace(/-/g, '') || `${Date.now()}_${Math.random().toString(36).slice(2)}`}`;
        const record = { key: keyFor(account, project, id), accountId: account, projectId: project, id,
          name: file.name || 'document', size: file.size, mime_type: file.type || '', category,
          queuedAt: new Date().toISOString(), status: 'pending', blob: file.slice(0, file.size, file.type) };
        await driver.put(record);
        emit(account, project);
        return record;
      });
    },
    setActive(userId, projectId, id, enabled) {
      return serial(async () => {
        const account = accountKey(userId); const project = projectKey(projectId);
        const key = keyFor(account, project, id);
        if (enabled) {
          if (!(await (await store()).get(account, project, id))) return false;
          active.add(key);
        } else active.delete(key);
        emit(account, project);
        return true;
      });
    },
    markStatus(userId, projectId, id, status) {
      return serial(async () => {
        const account = accountKey(userId); const project = projectKey(projectId); const driver = await store();
        const record = await driver.get(account, project, id);
        if (!record) return;
        await driver.put({ ...record, status });
        emit(account, project);
      });
    },
    acknowledgeFile(userId, projectId, id, serverFile) {
      return serial(async () => {
        if (!serverFile?.id) throw new Error('Server file acknowledgement is required');
        const account = accountKey(userId); const project = projectKey(projectId);
        await (await store()).remove(account, project, id);
        active.delete(keyFor(account, project, id));
        emit(account, project, { serverFile });
      });
    },
    removeFile(userId, projectId, id) {
      return serial(async () => {
        const account = accountKey(userId); const project = projectKey(projectId);
        if (active.has(keyFor(account, project, id))) throw new Error('Upload in progress');
        await (await store()).remove(account, project, id);
        emit(account, project);
      });
    },
    clearAccount(userId) {
      return serial(async () => {
        const account = accountKey(userId);
        await (await store()).clear(account);
        emit(account, null);
      });
    },
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
  };
}

const repository = createProjectFileQueue();
export const listQueuedProjectFiles = (...args) => repository.listFiles(...args);
export const enqueueProjectFile = (...args) => repository.enqueueFile(...args);
export const markQueuedFileStatus = (...args) => repository.markStatus(...args);
export const markQueuedFileActive = (...args) => repository.setActive(...args);
export const acknowledgeProjectFile = (...args) => repository.acknowledgeFile(...args);
export const removeQueuedProjectFile = (...args) => repository.removeFile(...args);
export const clearAccountProjectFiles = (...args) => repository.clearAccount(...args);
export const subscribeToProjectFiles = (...args) => repository.subscribe(...args);
